(() => {
  const root = document.querySelector('[data-order-request]');
  if (!root) return;
  const api = root.dataset.apiOrigin;
  const notice = root.querySelector('[data-order-notice]');
  const content = root.querySelector('[data-order-content]');
  const items = root.querySelector('[data-order-items]');
  const summary = root.querySelector('[data-order-summary]');
  const next = root.querySelector('[data-order-next]');
  const refresh = root.querySelector('[data-order-refresh]');
  const sessionKey = 'triple-m-order-session:' + api + location.pathname;
  let session = '';
  let busy = false;
  const validToken = value => typeof value === 'string' && /^[A-Za-z0-9_-]{43}$/.test(value);
  const message = (text, kind = '') => {
    notice.textContent = text;
    notice.dataset.kind = kind;
  };
  const dollars = cents => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
  }).format(cents / 100);
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const saveSession = value => {
    session = value;
    try {
      if (value) sessionStorage.setItem(sessionKey, value);
      else sessionStorage.removeItem(sessionKey);
    } catch { /* The in-memory session still permits this page's refresh. */ }
  };
  const readSession = () => {
    try { return sessionStorage.getItem(sessionKey) || ''; }
    catch { return ''; }
  };
  const request = async (path, options = {}) => {
    const response = await fetch(api + '/v1/customer/' + path, {
      mode: 'cors', cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer', ...options,
      headers: { ...(options.body ? { 'content-type': 'application/json' } : {}), ...options.headers },
    });
    if (!response.ok) {
      const error = new Error('Order status could not be verified.');
      error.expired = response.status === 401 || response.status === 403;
      throw error;
    }
    return response.json();
  };
  const refundAmounts = order => {
    const transactions = order.externalRefund?.transactions || [];
    return {
      confirmed: transactions.filter(row => row.status === 'SUCCESS').reduce((sum, row) => sum + row.amountCents, 0),
      pending: transactions.filter(row => row.status === 'PENDING').reduce((sum, row) => sum + row.amountCents, 0),
      failed: transactions.some(row => row.status === 'FAILURE'),
    };
  };
  const refundStatus = (order, refund) => {
    if (refund.pending) return 'Refund pending';
    if (refund.confirmed) return refund.confirmed >= order.card ? 'Refunded' : 'Partially refunded';
    return refund.failed ? 'Refund needs review' : '';
  };
  const orderStatus = (order, refund) => {
    const refunded = refundStatus(order, refund);
    if (refunded) return refunded;
    if (order.payment === 'failed') return 'Payment unsuccessful';
    if (order.payment === 'uncertain') return 'Checking payment';
    if (order.fulfillment === 'collected') return 'Collected';
    if (order.fulfillment === 'shipped') return 'Shipped';
    if (order.fulfillment === 'ready' && order.payment === 'paid') return 'Ready for pickup';
    if (order.lines.some(line => line.status === 'pending')) return 'Awaiting Store review';
    const approved = order.lines.filter(line => line.status === 'approved').length;
    if (!approved) return order.lines.some(line => line.status === 'unavailable') ? 'Declined' : 'Cancelled';
    if (order.payment !== 'paid') return 'Payment processing';
    return approved < order.lines.length ? 'Partially approved' : 'Approved';
  };
  const lineStatus = line => ({ pending: 'Awaiting Store review', approved: 'Approved',
    unavailable: 'Unavailable · no charge', cancelled: 'Cancelled · no charge' })[line.status];
  const safeHttps = value => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
    } catch { return ''; }
  };
  const renderItems = order => {
    items.replaceChildren();
    for (const line of order.lines) {
      const row = element('li', '', 'order-request__item');
      const imageUrl = line.image && safeHttps(line.image);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl; image.alt = ''; image.loading = 'lazy'; image.referrerPolicy = 'no-referrer';
        row.append(image);
      }
      const main = element('div', '', 'order-request__item-main');
      main.append(element('strong', line.name), element('small', `Qty ${line.quantity} · ${lineStatus(line)}`));
      row.append(main, element('span', dollars(line.price * line.quantity), 'order-request__item-price'));
      items.append(row);
    }
  };
  const fact = (label, value) => {
    const row = document.createElement('div');
    row.append(element('dt', label), element('dd', value));
    summary.append(row);
  };
  const renderSummary = (order, refund) => {
    const retained = order.lines.filter(line => ['pending', 'approved'].includes(line.status));
    const subtotal = retained.reduce((sum, line) => sum + line.price * line.quantity, 0);
    summary.replaceChildren();
    fact('Items retained', dollars(subtotal));
    fact('Shipping', order.delivery === 'pickup' ? 'Store pickup' : dollars(order.shipping));
    fact('Tax', dollars(order.tax || 0));
    fact(order.payment === 'paid' ? 'Original paid total' : 'Remaining order amount',
      dollars(order.payment === 'paid' ? order.card + order.credit : subtotal + (retained.length ? order.shipping + (order.tax || 0) : 0)));
    if (refund.confirmed) fact('Confirmed refund', dollars(refund.confirmed));
    if (refund.pending) fact('Pending refund', dollars(refund.pending));
    if (refund.confirmed) fact('Paid after confirmed refunds', dollars(order.card + order.credit - refund.confirmed));
  };
  const renderNext = (order, refund) => {
    next.replaceChildren();
    const refunded = refundStatus(order, refund);
    if (refunded) {
      next.append(element('h3', refunded), element('p', refund.pending
        ? 'Your refund is still processing. Your original payment is shown above.'
        : 'Your original payment and any confirmed refund are shown above.'));
      next.append(element('p', 'Contact the Store about any remaining pickup or shipment.'));
      return;
    }
    if (order.payment === 'authorized') {
      next.append(element('h3', 'Awaiting Store review'), element('p',
        'A payment authorization is not a completed charge. You will not be charged for unavailable items.'));
      return;
    }
    if (['pending', 'uncertain', 'failed'].includes(order.payment)) {
      next.append(element('h3', 'Payment update'), element('p', order.payment === 'failed'
        ? 'Payment was unsuccessful. Contact the Store before arranging pickup or shipping.'
        : 'We are checking the original payment result. Please do not place another payment for this order.'));
      return;
    }
    if (order.payment === 'void') {
      next.append(element('h3', 'Authorization released'), element('p',
        'No payment was collected. Your bank controls when any authorization hold disappears.'));
      return;
    }
    if (order.fulfillment === 'collected') {
      next.append(element('h3', 'Collected'), element('p', 'Your Store pickup is complete.'));
      return;
    }
    if (order.fulfillment === 'ready') {
      next.append(element('h3', 'Ready for pickup'), element('p', 'Bring your pickup code to the Store.'));
      if (order.pickupCode) next.append(element('p', order.pickupCode, 'order-request__pickup-code'));
      return;
    }
    if (order.fulfillment === 'shipped') {
      next.append(element('h3', 'On its way'), element('p', [order.carrier, order.tracking].filter(Boolean).join(' · ')));
      const trackingUrl = order.trackingUrl && safeHttps(order.trackingUrl);
      if (trackingUrl) {
        const link = element('a', 'Track package');
        link.href = trackingUrl; link.target = '_blank'; link.rel = 'noopener noreferrer';
        next.append(link);
      }
      return;
    }
    next.append(element('h3', 'Preparing your order'), element('p', order.delivery === 'pickup'
      ? 'Wait for the Ready for pickup notice before visiting the Store.'
      : 'We will send your tracking information when your order ships.'));
  };
  const render = order => {
    const refund = refundAmounts(order);
    root.querySelector('[data-order-name]').textContent = order.name || order.id;
    root.querySelector('[data-order-status]').textContent = orderStatus(order, refund);
    renderItems(order); renderSummary(order, refund); renderNext(order, refund);
    content.hidden = false;
  };
  const showFailure = error => {
    if (error.expired) {
      saveSession(''); content.hidden = true;
      message('This order session has expired. Open the private order link from your email or contact the Store.', 'error');
    } else {
      message(content.hidden
        ? 'We could not open your order. Check your connection and open the private link again.'
        : 'We could not refresh your order. The status below is the last verified result. Check your connection and try Refresh status again.', 'error');
    }
  };
  const load = async token => {
    if (busy) return;
    busy = true; refresh.disabled = true;
    message('Checking your order…');
    try {
      if (token) {
        const result = await request('session', { method: 'POST', body: JSON.stringify({ token }) });
        if (!validToken(result.session)) throw new Error('Invalid session');
        saveSession(result.session); render(result.order);
      } else {
        render(await request('order', { headers: { authorization: 'Bearer ' + session } }));
      }
      message('Your latest order status is shown below. Contact the Store if you need help.');
    } catch (error) { showFailure(error); }
    finally { busy = false; refresh.disabled = !session; }
  };
  refresh.addEventListener('click', () => { if (session) load(); });
  const token = location.hash.slice(1);
  history.replaceState(null, '', location.pathname + location.search);
  if (token) {
    saveSession('');
    if (validToken(token)) load(token);
    else message('Open the private order link from your email or contact the Store.', 'error');
  } else {
    session = readSession();
    if (validToken(session)) load();
    else { saveSession(''); message('Open the private order link from your email or contact the Store.', 'error'); }
  }
})();
